import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { fromMinutesToMilliseconds, TelemetryService } from "@obg/ngx.global";
import { RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { find } from "lodash-es";
import { EMPTY, interval } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { AUTH_ACTION_TYPE } from "../auth/auth.action";
import { SESSION_ACTION_TYPE } from "../auth/session.action";
import { CustomerManagementAction } from "../customer-management/customer-management.action";
import { CustomerDetailSection, KycState } from "../customer-management/customer-management.model";
import { CORE_ROUTE } from "../route";
import { CoreState } from "../state";

import { CustomerComplianceService } from "./customer-compliance.service";
import { KYC_ACTIONS_ACTION_TYPE } from "./kyc-actions.action";
import { KycActionsGuard } from "./kyc-actions.guard";
import { KycRealTimeChange, KycStatusType } from "./kyc-actions.model";
import { KycMessageService } from "./kyc-message.service";

@Injectable()
export class KycActionsEffect {
	activateKycOnSoftRefresh$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginSuccess,
					SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
					SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
					SESSION_ACTION_TYPE.loginWithTokenSuccess
				),
				concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
				filter(([, current]) => !!current && this.isRouteKYC(current.name)),
				switchMap(([, current]) =>
					this.customerComplianceService.kycStatusCheck$(current!.data!.actionType).pipe(
						take(1),
						filter(
							(kyc) =>
								(!kyc.isAllowed || kyc.currentStatus !== KycState.Accepted) &&
								!this.customerComplianceService.evaluateIsDutchKycRequested(
									kyc.complianceType,
									kyc.currentStatus
								)
						),
						tap((kyc) => {
							// for dutch kyc a redirect to email verification happens in common on first login service.
							this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
							this.kycMessageService.showMessage(kyc);
						})
					)
				)
			),
		{ dispatch: false }
	);

	customerComplianceCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			switchMap(() => this.customerComplianceService.getCurrentCompliance$()),
			// don't put kycState check into rxjs filter. polling should stop when state changes
			switchMap((compliance) =>
				compliance.kycState === KycState.Requested
					? interval(fromMinutesToMilliseconds(5)).pipe(
							map(() =>
								batchActions(
									this.customerAction.getCustomerDetail(CustomerDetailSection.AllowedActions),
									this.customerAction.getCustomerDetail(CustomerDetailSection.Kyc)
								)
							)
						)
					: EMPTY
			)
		)
	);

	verificationBannerDutch$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AUTH_ACTION_TYPE.sessionStart),
				switchMap(() => this.customerComplianceService.getCurrentCompliance$()),
				filter((compliance) =>
					this.customerComplianceService.evaluateIsDutchKycRequested(compliance.type, compliance.kycState)
				),
				tap((compliance) => {
					this.telemetryService.track({
						event: GtmEventAction.VerificationBarShown,
						RegistrationType: compliance.jurisdiction,
					});
				})
			),
		{ dispatch: false }
	);

	kycRealTimeStatusChange$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload>(KYC_ACTIONS_ACTION_TYPE.kycStatusChange),
			map((x) => x.payload),
			filter((payload: KycRealTimeChange) => this.checkForKYCActionChange(payload)),
			map(() => this.customerAction.getCustomerDetail())
		)
	);

	customerActionsRealTime$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload>(KYC_ACTIONS_ACTION_TYPE.customerActionsChange),
			map(() => this.customerAction.getCustomerDetail())
		)
	);

	constructor(
		private kycMessageService: KycMessageService,
		private routeService: RouteService,
		private store: Store<CoreState>,
		private actions$: Actions,
		private customerComplianceService: CustomerComplianceService,
		private customerAction: CustomerManagementAction,
		private telemetryService: TelemetryService
	) {}

	private isRouteKYC(routeName: string): boolean {
		const route = this.routeService.getRouteConfig(routeName);
		return !!route && !!find(route.canActivate, (token) => token === KycActionsGuard);
	}

	private checkForKYCActionChange(payload: KycRealTimeChange) {
		const { kycStatus, kycStatusDetails: kycSubStatus } = payload;
		return (
			(this.isAccepted(kycSubStatus.age) &&
				this.isAccepted(kycSubStatus.name) &&
				this.isAccepted(kycSubStatus.address)) ||
			this.isAccepted(kycStatus) ||
			this.isAccepted(kycSubStatus.bank)
		);
	}

	private isAccepted(value: string) {
		return !!value && value.toLowerCase().includes(KycStatusType.Accepted);
	}
}
